import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BOOK_CALL_URL, YOUTUBE_CHANNEL_URL } from "@/lib/links";
import ThemeToggle from "@/components/theme/ThemeToggle";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "What We Do", href: "#what-i-do" },
    { label: "Process", href: "#process" },
    { label: "Projects", href: "#projects" },
    { label: "YouTube", href: YOUTUBE_CHANNEL_URL, target: "_blank" },
  ];

  // Smooth-scroll to an in-page anchor, retrying briefly so a lazy section that
  // is still mounting becomes reachable.
  const scrollToHash = (hash: string, attempt = 0) => {
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (attempt < 10) {
      window.setTimeout(() => scrollToHash(hash, attempt + 1), 100);
    }
  };

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        // On a non-home route (e.g. /privacy), go home first, then scroll.
        navigate("/");
        window.setTimeout(() => scrollToHash(href), 120);
      } else {
        scrollToHash(href);
      }
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ink/70 backdrop-blur-xl backdrop-saturate-150 border-b border-ink-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
            : "bg-transparent"
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-3 cursor-pointer group"
            onClick={(e) => { e.preventDefault(); goHome(); }}
          >
            <img
              src="/logo.svg"
              alt="DOO MADE"
              className="h-8 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                // Over the hero photo (unscrolled) use crisp chalk so links read
                // on any imagery; once the solid bar appears, ease back to muted.
                className={`text-sm font-mono transition-colors duration-200 tracking-wide cursor-pointer whitespace-nowrap hover:text-acid-soft ${
                  scrolled ? "text-chalk-2 hover:text-chalk" : "text-chalk"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            <ThemeToggle />
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 bg-acid text-white text-sm font-semibold tracking-tight rounded-sm hover:bg-acid-hover transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Book a Fit Call
            </a>
          </div>

          {/* Mobile: theme toggle + menu */}
          <div className="md:hidden flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
            >
              <span className={`block w-6 h-px bg-chalk transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-1.5" : ""}`} />
              <span className={`block w-6 h-px bg-chalk transition-all duration-300 ${mobileOpen ? "opacity-0" : ""}`} />
              <span className={`block w-6 h-px bg-chalk transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-ink pt-16 flex flex-col px-6 py-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {navLinks.map((link, i) => (
              <motion.button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                className="py-4 text-left text-2xl font-semibold text-chalk border-b border-ink-3 cursor-pointer whitespace-nowrap"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.07 }}
              >
                {link.label}
              </motion.button>
            ))}
            <motion.a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="mt-8 px-6 py-4 bg-acid text-white text-lg font-bold tracking-tight rounded-sm cursor-pointer whitespace-nowrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Book an AI Workflow Fit Call
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
